import axios from 'axios';
import config from '../utils/config';

// Remove the /api suffix since config.api.baseURL already includes it
const API_BASE_URL = config.api.baseURL.replace(/\/api$/, '');

export const pageService = {
  // Get page content by slug
  getPageContent: async (slug) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/pages/${slug}`, {
        timeout: 5000, // 5s max — falls back to hardcoded content if slow
      });
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching page content for ${slug}:`, error);
      
      // Return fallback content if API fails
      if (error.response?.status === 404) {
        console.warn(`Page '${slug}' not found in CMS, using fallback content`);
        return null;
      }
      
      throw error;
    }
  },

  // Get all pages
  getAllPages: async (includeUnpublished = false) => {
    try {
      const params = includeUnpublished ? { includeUnpublished: 'true' } : {};
      const response = await axios.get(`${API_BASE_URL}/api/pages`, { params });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching all pages:', error);
      throw error;
    }
  },

  // Get pages by status
  getPagesByStatus: async (status) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/pages/status/${status}`);
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching pages with status ${status}:`, error);
      throw error;
    }
  },

  // Search pages
  searchPages: async (query) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/pages/search/${encodeURIComponent(query)}`);
      return response.data.data;
    } catch (error) {
      console.error('Error searching pages:', error);
      throw error;
    }
  },

  // Get contact information
  getContactInfo: async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/contact-info`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching contact info:', error);
      throw error;
    }
  },

  // Get setting by key
  getSetting: async (key) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/settings/${key}`);
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching setting ${key}:`, error);
      throw error;
    }
  },

  // Get all settings
  getAllSettings: async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/settings`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching settings:', error);
      throw error;
    }
  },

  // Admin methods (for dashboard)
  admin: {
    // Create page content
    createPageContent: async (data) => {
      try {
        const response = await axios.post(`${API_BASE_URL}/api/pages`, data);
        return response.data;
      } catch (error) {
        console.error('Error creating page content:', error);
        throw error;
      }
    },

    // Update page content
    updatePageContent: async (id, data) => {
      try {
        const response = await axios.put(`${API_BASE_URL}/api/pages/${id}`, data);
        return response.data;
      } catch (error) {
        console.error('Error updating page content:', error);
        throw error;
      }
    },

    // Delete page content
    deletePageContent: async (id) => {
      try {
        const response = await axios.delete(`${API_BASE_URL}/api/pages/${id}`);
        return response.data;
      } catch (error) {
        console.error('Error deleting page content:', error);
        throw error;
      }
    },

    // Publish page
    publishPage: async (id) => {
      try {
        const response = await axios.put(`${API_BASE_URL}/api/pages/${id}/publish`);
        return response.data;
      } catch (error) {
        console.error('Error publishing page:', error);
        throw error;
      }
    },

    // Unpublish page
    unpublishPage: async (id) => {
      try {
        const response = await axios.put(`${API_BASE_URL}/api/pages/${id}/unpublish`);
        return response.data;
      } catch (error) {
        console.error('Error unpublishing page:', error);
        throw error;
      }
    },

    // Contact info management
    createContactInfo: async (data) => {
      try {
        const response = await axios.post(`${API_BASE_URL}/api/contact-info`, data);
        return response.data;
      } catch (error) {
        console.error('Error creating contact info:', error);
        throw error;
      }
    },

    updateContactInfo: async (id, data) => {
      try {
        const response = await axios.put(`${API_BASE_URL}/api/contact-info/${id}`, data);
        return response.data;
      } catch (error) {
        console.error('Error updating contact info:', error);
        throw error;
      }
    },

    deleteContactInfo: async (id) => {
      try {
        const response = await axios.delete(`${API_BASE_URL}/api/contact-info/${id}`);
        return response.data;
      } catch (error) {
        console.error('Error deleting contact info:', error);
        throw error;
      }
    },

    // Settings management
    createSetting: async (data) => {
      try {
        const response = await axios.post(`${API_BASE_URL}/api/settings`, data);
        return response.data;
      } catch (error) {
        console.error('Error creating setting:', error);
        throw error;
      }
    },

    updateSetting: async (key, value, updatedBy) => {
      try {
        const response = await axios.put(`${API_BASE_URL}/api/settings/${key}`, {
          value,
          updatedBy
        });
        return response.data;
      } catch (error) {
        console.error('Error updating setting:', error);
        throw error;
      }
    },

    deleteSetting: async (key) => {
      try {
        const response = await axios.delete(`${API_BASE_URL}/api/settings/${key}`);
        return response.data;
      } catch (error) {
        console.error('Error deleting setting:', error);
        throw error;
      }
    }
  }
};

export default pageService;